export type JsonSchemaType =
  | "null"
  | "boolean"
  | "object"
  | "array"
  | "number"
  | "string"
  | "integer";

type JsonSchemaBaseProperty = {
  $ref?: string;
  title?: string;
  description?: string;
  default?: unknown;
  examples?: unknown[];
  deprecated?: boolean;
  pattern?: string;
};

export type JsonSchemaLeafProperty = JsonSchemaBaseProperty & {
  type?: JsonSchemaType | JsonSchemaType[];
  format?: string;
  enum?: unknown[];
  const?: unknown;

  // String constraints
  minLength?: number;
  maxLength?: number;

  // Number constraints
  minimum?: number;
  maximum?: number;
  exclusiveMinimum?: number;
  exclusiveMaximum?: number;
  multipleOf?: number;
};

// - Composite
export type JsonSchemaObjectProperty = JsonSchemaBaseProperty & {
  type: "object" | ("object" | "null")[]; // allow nullable object
  properties?: Record<string, JsonSchemaNode>;
  required?: string[];
  additionalProperties?: boolean | JsonSchemaNode;
};

export type JsonSchemaArrayDraft7 = JsonSchemaBaseProperty & {
  type: "array" | ("array" | "null")[]; // allow nullable array
  items?: JsonSchemaNode | JsonSchemaNode[];
  additionalItems?: boolean | JsonSchemaNode;
  minItems?: number;
  maxItems?: number;
  uniqueItems?: boolean;
};

export type JsonSchemaArray2020 = JsonSchemaBaseProperty & {
  type: "array" | ("array" | "null")[]; // allow nullable array
  items?: JsonSchemaNode;
  prefixItems?: JsonSchemaNode[];
  minItems?: number;
  maxItems?: number;
  uniqueItems?: boolean;
};

export type JsonSchemaCompositeProperty =
  | JsonSchemaObjectProperty
  | JsonSchemaArrayDraft7
  | JsonSchemaArray2020;

export type JsonSchemaUnionProperty = JsonSchemaBaseProperty & {
  anyOf?: JsonSchemaNode[];
  oneOf?: JsonSchemaNode[];
  allOf?: JsonSchemaNode[];
  not?: JsonSchemaNode;
};

export type JsonSchemaNode =
  | JsonSchemaLeafProperty
  | JsonSchemaCompositeProperty
  | JsonSchemaUnionProperty;

export type JsonSchema = JsonSchemaBaseProperty & {
  $schema?: string;
  $id?: string;

  type?: JsonSchemaType | JsonSchemaType[];
  properties: Record<string, JsonSchemaNode>;
  required?: string[];

  $defs?: Record<string, JsonSchemaDefinition>; // 2020-12 - can be any schema
  definitions?: Record<string, JsonSchemaDefinition>; // Draft-07 - can be any schema
};

// Schemas in $defs can be either full schemas or simpler nodes
export type JsonSchemaDefinition = JsonSchema | JsonSchemaNode;

// - Type Guards

export const isObject = (
  property: JsonSchemaNode,
): property is JsonSchemaCompositeProperty & { type: "object" } =>
  hasExactType(property, "object");

export const isObjectWithProperties = (
  property: JsonSchemaNode,
): property is JsonSchemaCompositeProperty & {
  type: "object";
  properties: Record<string, JsonSchemaNode>;
} =>
  isObject(property) &&
  "properties" in property &&
  !!property.properties &&
  Object.keys(property.properties).length > 0;

export const isArrayDraft7 = (
  property: JsonSchemaNode,
): property is JsonSchemaArrayDraft7 =>
  hasExactType(property, "array") &&
  ("items" in property || "additionalItems" in property);

export const isArray2020 = (
  property: JsonSchemaNode,
): property is JsonSchemaArray2020 =>
  hasExactType(property, "array") &&
  ("items" in property || "prefixItems" in property);

export const isArray = (
  property: JsonSchemaNode,
): property is JsonSchemaArrayDraft7 | JsonSchemaArray2020 =>
  isArrayDraft7(property) || isArray2020(property);

export const isArrayWithItems = (
  property: JsonSchemaNode,
): property is JsonSchemaCompositeProperty & {
  type: "array";
  items: JsonSchemaNode | JsonSchemaNode[];
} => isArray(property) && "items" in property && property.items != null;

export const isTupleArray = (
  property: JsonSchemaNode,
): property is
  | (JsonSchemaArrayDraft7 & { items: JsonSchemaNode[] })
  | (JsonSchemaArray2020 & { prefixItems: JsonSchemaNode[] }) =>
  (isArrayDraft7(property) &&
    Array.isArray(property.items) &&
    property.items.length > 0) ||
  (isArray2020(property) &&
    Array.isArray(property.prefixItems) &&
    property.prefixItems.length > 0);

export const isAnyOf = (
  property: JsonSchemaNode,
): property is JsonSchemaUnionProperty & { anyOf: JsonSchemaNode[] } =>
  Array.isArray((property as JsonSchemaUnionProperty).anyOf);

export const isOneOf = (
  property: JsonSchemaNode,
): property is JsonSchemaUnionProperty & { oneOf: JsonSchemaNode[] } =>
  Array.isArray((property as JsonSchemaUnionProperty).oneOf);

export const isUnion = (
  property: JsonSchemaNode,
): property is JsonSchemaUnionProperty =>
  isAnyOf(property) || isOneOf(property);

export const isArrayOfUnion = (
  property: JsonSchemaNode,
): property is JsonSchemaCompositeProperty & {
  type: "array";
  items: JsonSchemaUnionProperty;
} =>
  isArrayWithItems(property) &&
  !Array.isArray(property.items) &&
  isUnion(property.items);

export const isArrayOfPrimitive = (
  property: JsonSchemaNode,
): property is JsonSchemaCompositeProperty & {
  type: "array";
  items: JsonSchemaLeafProperty;
} =>
  isArray(property) &&
  !Array.isArray(property.items) && // exclude tuple form
  (isUntyped(property.items) ||
    ("items" in property && isPrimitive(property.items!))); // items with primitive type(s)

export const isArrayOfRef = (
  property: JsonSchemaNode,
): property is JsonSchemaCompositeProperty & {
  type: "array";
  items: JsonSchemaNode & { $ref: string };
} =>
  isArrayWithItems(property) &&
  !Array.isArray(property.items) &&
  property.items.$ref !== undefined;

export const isPrimitive = (
  property: JsonSchemaNode,
): property is JsonSchemaLeafProperty =>
  ["string", "number", "integer", "boolean", "null"].some((t) =>
    hasExactType(property, t),
  );

export const isPureUnion = (
  property: JsonSchemaNode,
): property is JsonSchemaUnionProperty =>
  !("type" in property) && !("properties" in property) && !property.$ref;

export const hasRequired = (
  property: JsonSchemaNode,
): property is JsonSchemaCompositeProperty & { required: string[] } =>
  "required" in property && !!property.required;

export const hasProperties = (
  property: JsonSchemaNode,
): property is JsonSchemaCompositeProperty & {
  properties: Record<string, JsonSchemaNode>;
} =>
  typeof property === "object" &&
  "properties" in property &&
  !!property.properties;

export const hasAdditionalProperties = (
  property: JsonSchemaNode,
): property is JsonSchemaCompositeProperty & {
  additionalProperties: JsonSchemaNode;
} =>
  typeof property === "object" &&
  "additionalProperties" in property &&
  !!property.additionalProperties;

export const isUntyped = (schema: JsonSchemaNode | undefined): boolean => {
  if (!schema) return true;
  if ("$ref" in schema) return false; // refs are not untyped

  // unions are not untyped
  if ("anyOf" in schema || "oneOf" in schema || "allOf" in schema) {
    return false;
  }

  const hasType =
    "type" in schema &&
    schema.type !== undefined &&
    (!Array.isArray(schema.type) || schema.type.length > 0);
  const hasEnum = "enum" in schema;
  const hasProperties = "properties" in schema;
  const hasItems = "items" in schema;

  return !(hasType || hasEnum || hasProperties || hasItems);
};

export const isNullable = (property: JsonSchemaNode): boolean => {
  if (!("type" in property)) return false;
  if (!Array.isArray(property.type)) return property.type === "null";
  return property.type.includes("null");
};

export const isNullType = (node: JsonSchemaNode): boolean => {
  return "type" in node && node.type === "null";
};

// Helper to ensure the given schema is only of the given type & no other type
const hasExactType = (schema: JsonSchemaNode, expected: string): boolean =>
  ("type" in schema &&
    (schema.type === expected ||
      (Array.isArray(schema.type) &&
        schema.type.every((t) => t === expected || t === "null")))) ||
  ("$ref" in schema && schema.$ref == expected);

// Real unions are union types (oneOf/anyOf) that have more than one non-null member
// Note that older JSON schema versions used to specify nullable types as a union with "null"
export const isRealUnion = (property: JsonSchemaNode): boolean => {
  if (!isAnyOf(property) && !isOneOf(property)) {
    return false;
  }

  const members = isAnyOf(property) ? property.anyOf : property.oneOf;
  if (members.length <= 1) {
    return false;
  }
  if (members.length === 2) {
    const nullMembers = members.filter(isNullType);
    const nonNullMembers = members.filter((member) => !isNullType(member));

    if (nullMembers.length === 1 && nonNullMembers.length === 1) {
      const nonNullMember = nonNullMembers[0];

      // Check if the non-null member itself contains a union (nested union)
      // If it does, then this is a real union, not just nullable
      if (isAnyOf(nonNullMember) || isOneOf(nonNullMember)) {
        return true;
      }

      // Check for allOf as well (also indicates a complex union)
      // It's a real union if the non-null item has allOf, otherwise it's just old-style nullable
      const asUnion = nonNullMember as JsonSchemaUnionProperty;
      return Boolean(asUnion.allOf);
    }
  }

  return true; // 3+ items or other combinations are real unions
};

// Check if a union contains only primitive types (excluding null)
export const isUnionOfPrimitivesOnly = (property: JsonSchemaNode): boolean => {
  if (!isAnyOf(property) && !isOneOf(property)) {
    return false;
  }

  const members = isAnyOf(property) ? property.anyOf : property.oneOf;
  const nonNullMembers = members.filter((member) => !isNullType(member));
  return nonNullMembers.every(isPrimitive);
};
